import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { FileQuestion, ShieldAlert, TriangleAlert } from "lucide-react";

const content = {
  "not-found": {
    code: "404",
    title: "Page not found",
    description:
      "The page you’re looking for may have moved or the address may be incorrect.",
    icon: FileQuestion,
  },
  forbidden: {
    code: "403",
    title: "Access denied",
    description:
      "You don’t have permission to view this page. Contact your administrator if you need access.",
    icon: ShieldAlert,
  },
  unexpected: {
    code: "Something went wrong",
    title: "We couldn’t load this page",
    description:
      "Please reload the page and try again. If the problem continues, contact your administrator.",
    icon: TriangleAlert,
  },
};

export function ErrorPage({
  kind,
  homeHref = "/dashboard",
  homeLabel = "Return to dashboard",
}: {
  kind: keyof typeof content;
  homeHref?: string;
  homeLabel?: string;
}) {
  const { code, title, description, icon: Icon } = content[kind];
  return (
    <Box
      sx={{
        minHeight: "65vh",
        display: "grid",
        placeItems: "center",
        p: { xs: 2, sm: 4 },
      }}
    >
      <Paper
        component="section"
        aria-labelledby="error-page-title"
        sx={{
          width: "100%",
          maxWidth: 560,
          p: { xs: 3, sm: 5 },
          textAlign: "center",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Box sx={{ color: "primary.main" }}>
            <Icon size={44} aria-hidden="true" />
          </Box>
          <Typography color="text.secondary" fontWeight={700}>
            {code}
          </Typography>
          <Typography id="error-page-title" component="h1" variant="h2">
            {title}
          </Typography>
          <Typography color="text.secondary">{description}</Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap", justifyContent: "center", pt: 1 }}
          >
            {kind === "unexpected" && (
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
              >
                Reload page
              </Button>
            )}
            <Button
              component="a"
              href={homeHref}
              variant={kind === "unexpected" ? "outlined" : "contained"}
            >
              {homeLabel}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
